import { 
  doc, 
  collection, 
  setDoc, 
  deleteDoc, 
  onSnapshot, 
  serverTimestamp, 
  query, 
  where,
  getDocs,
  updateDoc,
  deleteField
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface UserPresence {
  userId: string;
  displayName: string;
  isOnline: boolean;
  lastSeen: any;
  currentPage: string;
  groupId?: string;
}

export interface GroupPresence {
  groupId: string;
  onlineCount: number;
  onlineUsers: string[];
}

export class PresenceService {
  private currentUserId: string | null = null;
  private presenceDoc: any = null;
  private unsubscribePresence: (() => void) | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private visibilityTimer: NodeJS.Timeout | null = null;
  private isTabVisible: boolean = true;
  private lastHeartbeat: number = 0;
  private listeners: Map<string, () => void> = new Map();

  // Performance: Configurable intervals
  private readonly HEARTBEAT_INTERVAL = 45000; // 45 seconds
  private readonly OFFLINE_DELAY = 10000; // 10 seconds delay before offline
  private readonly MIN_HEARTBEAT_INTERVAL = 30000; // Minimum time between heartbeats

  // Set user as online
  async setUserOnline(userId: string, displayName: string, currentPage: string, groupId?: string) {
    this.currentUserId = userId;
    const presenceRef = doc(db, 'presence', userId);
    
    // Build presence object, only including groupId if it's defined
    const presence: any = {
      userId,
      displayName,
      isOnline: true,
      lastSeen: serverTimestamp(),
      currentPage
    };

    // Only add groupId if it's not undefined (Firestore doesn't allow undefined values)
    if (groupId !== undefined) {
      presence.groupId = groupId;
    }

    await setDoc(presenceRef, presence);
    this.presenceDoc = presenceRef;
    this.lastHeartbeat = Date.now();

    // Start heartbeat to keep presence alive
    this.startHeartbeat();

    // Set up disconnect handler
    this.setupDisconnectHandler();
  }

  // Update user's current page/group
  async updateUserLocation(currentPage: string, groupId?: string, forceUpdate: boolean = false) {
    if (!this.currentUserId) return;

    // Performance: Throttle updates to prevent excessive writes, but allow force updates for group transitions
    const now = Date.now();
    if (!forceUpdate && now - this.lastHeartbeat < this.MIN_HEARTBEAT_INTERVAL) {
      return;
    }

    const presenceRef = doc(db, 'presence', this.currentUserId);
    
    // Build update object
    const updateData: any = {
      currentPage,
      lastSeen: serverTimestamp()
    };

    // Handle groupId: set it if defined, or remove it if undefined
    if (groupId !== undefined) {
      updateData.groupId = groupId;
    } else {
      // When leaving a group, we need to explicitly remove the groupId field
      updateData.groupId = deleteField();
    }
    
    await updateDoc(presenceRef, updateData);
    this.lastHeartbeat = now;
  }

  // Set user as offline
  async setUserOffline() {
    if (!this.currentUserId) return;

    const presenceRef = doc(db, 'presence', this.currentUserId);
    try {
      await deleteDoc(presenceRef);
    } catch (error) {
      console.warn('Error removing presence:', error);
    }

    this.cleanup();
  }

  // Listen to online users on community page (includes users in groups)
  onCommunityOnlineUsersChange(callback: (users: UserPresence[]) => void) {
    const listenerId = 'community';
    
    // Performance: Clean up existing listener if any
    if (this.listeners.has(listenerId)) {
      this.listeners.get(listenerId)?.();
    }

    // Query for all online users on community page (including those in groups)
    const presenceQuery = query(
      collection(db, 'presence'),
      where('isOnline', '==', true)
    );

    const unsubscribe = onSnapshot(presenceQuery, (snapshot) => {
      const users: UserPresence[] = [];
      snapshot.forEach((doc) => {
        const userData = doc.data() as UserPresence;
        // Include users who are on community page OR in a group
        if (userData.currentPage === 'community' || userData.currentPage === 'group') {
          users.push(userData);
        }
      });
      callback(users);
    }, (error) => {
      console.error('Error listening to community presence:', error);
    });

    this.listeners.set(listenerId, unsubscribe);
    return unsubscribe;
  }

  // Listen to online users in a specific group
  onGroupOnlineUsersChange(groupId: string, callback: (users: UserPresence[]) => void) {
    const listenerId = `group-${groupId}`;
    
    // Performance: Clean up existing listener if any
    if (this.listeners.has(listenerId)) {
      this.listeners.get(listenerId)?.();
    }

    const presenceQuery = query(
      collection(db, 'presence'),
      where('isOnline', '==', true),
      where('groupId', '==', groupId)
    );

    const unsubscribe = onSnapshot(presenceQuery, (snapshot) => {
      const users: UserPresence[] = [];
      snapshot.forEach((doc) => {
        const userData = doc.data() as UserPresence;
        users.push(userData);
      });
      callback(users);
    }, (error) => {
      console.error(`Error listening to group ${groupId} presence:`, error);
    });

    this.listeners.set(listenerId, unsubscribe);
    return unsubscribe;
  }

  // Get all online users
  async getAllOnlineUsers(): Promise<UserPresence[]> {
    const presenceQuery = query(
      collection(db, 'presence'),
      where('isOnline', '==', true)
    );

    const snapshot = await getDocs(presenceQuery);
    const users: UserPresence[] = [];
    snapshot.forEach((doc) => {
      users.push(doc.data() as UserPresence);
    });
    return users;
  }

  // Get online count for community (includes users in groups)
  async getCommunityOnlineCount(): Promise<number> {
    const presenceQuery = query(
      collection(db, 'presence'),
      where('isOnline', '==', true)
    );

    const snapshot = await getDocs(presenceQuery);
    let count = 0;
    snapshot.forEach((doc) => {
      const userData = doc.data() as UserPresence;
      if (userData.currentPage === 'community' || userData.currentPage === 'group') {
        count++;
      }
    });
    return count;
  }

  // Get online count for specific group
  async getGroupOnlineCount(groupId: string): Promise<number> {
    const presenceQuery = query(
      collection(db, 'presence'),
      where('isOnline', '==', true),
      where('groupId', '==', groupId)
    );

    const snapshot = await getDocs(presenceQuery);
    return snapshot.size;
  }

  // Start heartbeat to keep presence alive
  private startHeartbeat() {
    this.heartbeatInterval = setInterval(async () => {
      // Performance: Only send heartbeat if tab is visible and user is active
      if (!this.isTabVisible || !this.presenceDoc || !this.currentUserId) {
        return;
      }

      try {
        await updateDoc(this.presenceDoc, {
          lastSeen: serverTimestamp()
        });
        this.lastHeartbeat = Date.now();
      } catch (error) {
        console.warn('Heartbeat failed:', error);
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Setup disconnect handler
  private setupDisconnectHandler() {
    // Handle page unload
    const handleBeforeUnload = () => {
      // Performance: Use sendBeacon for reliable offline signal
      if (navigator.sendBeacon && this.currentUserId) {
        navigator.sendBeacon('/api/presence/offline', JSON.stringify({ userId: this.currentUserId }));
      }
      this.setUserOffline();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Handle visibility change with improved performance
    const handleVisibilityChange = () => {
      this.isTabVisible = !document.hidden;
      
      if (document.hidden) {
        // Performance: Longer delay to prevent rapid online/offline cycling
        this.visibilityTimer = setTimeout(() => {
          if (document.hidden) {
            this.setUserOffline();
          }
        }, this.OFFLINE_DELAY);
      } else {
        // Tab became visible again
        if (this.visibilityTimer) {
          clearTimeout(this.visibilityTimer);
          this.visibilityTimer = null;
        }
        // Restart presence if needed
        if (!this.presenceDoc && this.currentUserId) {
          // User came back, restore presence
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle online/offline events
    const handleOffline = () => {
      this.setUserOffline();
    };
    window.addEventListener('offline', handleOffline);

    // Performance: Cleanup event listeners
    this.setupCleanupListeners = () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('offline', handleOffline);
    };
  }

  private setupCleanupListeners?: () => void;

  // Cleanup with improved performance
  private cleanup() {
    // Clear heartbeat
    this.stopHeartbeat();

    // Clear visibility timer
    if (this.visibilityTimer) {
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = null;
    }

    // Cleanup all listeners
    this.listeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.listeners.clear();

    // Cleanup event listeners
    if (this.setupCleanupListeners) {
      this.setupCleanupListeners();
    }

    this.currentUserId = null;
    this.presenceDoc = null;
  }

  // Performance: Method to manually cleanup listeners for specific groups
  cleanupGroupListener(groupId: string) {
    const listenerId = `group-${groupId}`;
    const unsubscribe = this.listeners.get(listenerId);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(listenerId);
    }
  }
}

// Export singleton instance
export const presenceService = new PresenceService(); 